import { forwardRef, type SelectHTMLAttributes } from 'react';
import { ChevronDown } from 'lucide-react';
import { controlClass } from '../lib/control.js';
import { resolveOptionsLoadState, type OptionsLoadState } from '../lib/optionsLoadState.js';
import { cn } from '../lib/utils.js';

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  /** Show error styling. Sets `aria-invalid` for assistive tech to match. */
  error?: boolean;
  /** Optional placeholder rendered as a disabled, value-empty `<option>` when no value is selected. */
  placeholder?: string;
  /**
   * Whether the OPTIONS are loading or failed to load — see
   * `lib/optionsLoadState.ts`. A failed list blocks the control and says so in
   * place of the placeholder, rather than rendering as a picker with nothing
   * in it. Omit it and nothing changes.
   */
  loadState?: OptionsLoadState;
}

/* ===========================================================================
 * THE CHEVRON — and why it is a real element rather than a background image.
 *
 * This control strips the browser's own arrow (`appearance-none`) so it can
 * carry the same border, corners, height and focus treatment as `<Input>`.
 * Something has to put an arrow back, or a select is indistinguishable from a
 * text field — which is exactly the state this component shipped in.
 *
 * It used to draw the replacement with
 *   bg-[url('data:image/svg+xml;utf8,<svg …stroke="currentColor"…>')]
 * and that line was broken twice over:
 *
 *  1. IT NEVER COMPILED. Tailwind discards an arbitrary value it cannot parse,
 *     and the raw `<`, `>` and spaces in that data URI made it one of them. The
 *     failure is silent and asymmetric: the SIBLING background utilities on the
 *     same element (`bg-no-repeat`, `bg-[right_1rem_center]`) generated fine, so
 *     the stylesheet looked plausible while `background-image` computed to
 *     `none`. Compiling the web app's CSS and grepping it for `data:image/svg`
 *     returned zero hits, against a live `background-position: right 1rem
 *     center` — that asymmetry is what makes the bug so easy to miss in review.
 *
 *  2. EVEN COMPILED, IT COULD NOT FOLLOW THE THEME. A background image is a
 *     separate document. It cannot see the element's `color`, so
 *     `stroke="currentColor"` resolves to black — in BOTH themes. Any data-URI
 *     arrow has to hard-code a hue, and apps/web has no raw palette colours.
 *
 *  3. IT TOOK `bg-surface` DOWN WITH IT. The data URI contains spaces, and a
 *     class attribute is whitespace-separated — so that one "class" reached
 *     `cn`/tailwind-merge as a dozen nonsense fragments, several of them
 *     `bg-…`-shaped. Merge resolved them against the real background utility
 *     and `bg-surface` never made it to the DOM: rendering HEAD's component in
 *     a browser gives a select with the UA's default white, in dark mode too.
 *     Fixing the chevron fixes the field's background as a side effect.
 *
 * So the arrow is an inline `<svg>` overlaid on the control. It inherits
 * `currentColor` from `text-muted-foreground` for real, which means one class
 * covers light, dark and any tenant re-brand. Do not "simplify" this back into
 * a background image.
 *
 * WHY THE WRAPPER IS SAFE. An overlay needs a positioned ancestor, and a
 * `<select>` cannot hold one (it has no usable pseudo-elements and its children
 * are `<option>`s). So the control is wrapped — and a wrapper is the one thing
 * that could break the ~40 call sites, because they size the control through
 * `className` (`w-auto`, `w-full sm:w-auto`, `w-full md:w-56`, `w-48`) and that
 * width has to act on whatever box the parent lays out. Two rules keep the
 * geometry identical to the bare `<select>`:
 *
 *   - Utilities that size or place the control INSIDE ITS PARENT are hoisted
 *     onto the wrapper (see `splitOuterBoxClasses`); everything else — borders,
 *     tints, `rounded-full` pills, type — stays on the `<select>`, which then
 *     simply fills the wrapper. So `w-auto` still shrink-wraps and the
 *     `bg-warning-surface` variant on the devices page still tints the field.
 *   - The wrapper is `inline-block`, not `block`. A block-level `w-auto` box
 *     fills its line; a `<select>` shrink-wraps. `inline-block` reproduces
 *     both, and `align-bottom` keeps the inline box off the text baseline so no
 *     descender gap creeps into a `space-y-*` form column.
 *
 * Measured against the previous markup across every container the app uses
 * (form column, `flex flex-wrap` toolbar, grid cell, `flex-1` cell) and every
 * className the app passes: identical width, height and column height in all 28
 * combinations. `block` diverged in 6 of them.
 * ======================================================================== */

/**
 * Utilities whose effect belongs to the OUTER box — the one the parent's flex
 * or grid lays out — rather than to the field's own skin. `flex` on its own is
 * deliberately absent: that is `controlClass`'s display, not a flex-item size.
 */
const OUTER_BOX_UTILITY =
  /^(?:w-|min-w-|max-w-|basis-|order-|col-|row-|self-|justify-self-|place-self-|grow(?:-|$)|shrink(?:-|$)|flex-(?:1|auto|none|grow|shrink|\[))/;

/**
 * Split a caller's `className` into [wrapper, select]. Variants (`sm:`, `md:`,
 * `dark:`, `[@media(pointer:coarse)]:`) don't change WHICH box a utility acts
 * on, so the decision is made on the bare utility at the end of the chain —
 * `sm:w-auto` is hoisted exactly like `w-auto`.
 */
function splitOuterBoxClasses(className: string | undefined): [string, string] {
  const outer: string[] = [];
  const inner: string[] = [];
  for (const token of (className ?? '').split(/\s+/)) {
    if (!token) continue;
    const bare = token.replace(/^!/, '').split(':').pop() ?? '';
    (OUTER_BOX_UTILITY.test(bare) ? outer : inner).push(token);
  }
  return [outer.join(' '), inner.join(' ')];
}

/**
 * Styled native `<select>`. Shares `controlClass` with `<Input>`, so the two
 * are the same height with the same border, corners and focus treatment and
 * line up when they sit in the same form row. The chevron is this component's
 * own — `controlClass` must stay free of it, or `<Input>` and `<Combobox>`
 * would grow an arrow they have no dropdown to open.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, error, placeholder, loadState, children, disabled, ...props }, ref) => {
    const [wrapperClass, selectClass] = splitOuterBoxClasses(className);
    const load = resolveOptionsLoadState(loadState, placeholder);
    return (
      // `w-full` first so `cn`'s tailwind-merge lets a caller's own width win;
      // a caller that only sets `max-w-*` still gets the full-width default.
      <span className={cn('relative inline-block w-full align-bottom', wrapperClass)}>
        <select
          ref={ref}
          disabled={disabled === true || load.blocked}
          className={cn(
            controlClass,
            // `peer` is here only so the chevron can dim with the control. It
            // reads the real `:disabled` state, which also covers a select
            // disabled by an ancestor `<fieldset disabled>` rather than by the
            // prop.
            'peer appearance-none',
            // Keeps the longest option's text off the chevron. Logical, so it
            // moves to the leading edge under `dir="rtl"` with the arrow.
            'pe-10',
            selectClass
          )}
          {...props}
          aria-invalid={error === true || load.failed ? true : props['aria-invalid']}
          // See `Input.tsx` — `required` is mirrored onto `aria-required` on
          // every control in the family so the rule holds everywhere.
          aria-required={props.required ? true : props['aria-required']}
        >
          {/* NOT `hidden` while blocked: the whole point is that the reader
              sees "could not load" sitting in the closed control. */}
          {load.placeholder !== undefined && (
            <option value="" disabled hidden={!load.blocked}>
              {load.placeholder}
            </option>
          )}
          {children}
        </select>
        <ChevronDown
          // Decorative: the native control already announces itself as a
          // listbox, so the arrow is noise to a screen reader.
          aria-hidden="true"
          // Size pinned in CSS as well as by the prop, so a call site that sets
          // `text-label` on the field (the report filter pills do) can't scale
          // the arrow with it.
          size={16}
          className={cn(
            'pointer-events-none absolute top-1/2 -translate-y-1/2 [width:16px] [height:16px]',
            // `end-4` matches the field's own 16px inline padding and flips
            // with `dir` — never `right-4`, which would sit over the text in
            // Arabic.
            'end-4',
            // The semantic token, so it re-inks itself for dark mode and for a
            // tenant brand. `currentColor` inside the SVG resolves against
            // this — the thing the old background image could never do.
            'text-muted-foreground',
            // Clicks belong to the select underneath; this only mirrors its
            // disabled fade — at the SAME 50% the field itself uses. It was
            // 70%, which left the arrow visibly brighter than the box it
            // belongs to: the one part of a disabled control that still looked
            // live. The chevron sits in the wrapper, not inside the `<select>`,
            // so it does not inherit the field's opacity and has to state it.
            'peer-disabled:opacity-50'
          )}
        />
      </span>
    );
  }
);
Select.displayName = 'Select';
